import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { getFrontFileUrl } from "../../services/apiFront";
import { formatParcours } from "../../services/aspirantUtils";

export default function AspirantsAdmin() {
  const [aspirants, setAspirants] = useState([]);
  const [validatedAspirants, setValidatedAspirants] = useState([]);
  const [rejectedAspirants, setRejectedAspirants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingValidated, setLoadingValidated] = useState(true);
  const [loadingRejected, setLoadingRejected] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedAspirant, setSelectedAspirant] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");
  const [progressCGByAspirant, setProgressCGByAspirant] = useState({});
  const [progressSYLByAspirant, setProgressSYLByAspirant] = useState({});
  const [loadingProgress, setLoadingProgress] = useState(false);

  useEffect(() => {
    loadAspirants();
    loadValidatedAspirants();
    loadRejectedAspirants();
  }, []);

  const loadAspirants = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/aspirants/pending-validation");
      const aspirantsData = res.data?.aspirants || [];
      setAspirants(aspirantsData);
      // Charger la progression après avoir chargé les aspirants
      if (aspirantsData.length > 0) {
        loadProgressForAspirants(aspirantsData);
      }
    } catch (err) {
      console.error("Erreur chargement aspirants:", err);
      setError(err?.response?.data?.error || "Erreur lors du chargement des élèves");
    } finally {
      setLoading(false);
    }
  };

  const loadValidatedAspirants = async () => {
    setLoadingValidated(true);
    try {
      const res = await api.get("/aspirants/validated");
      const aspirantsData = res.data?.aspirants || [];
      setValidatedAspirants(aspirantsData);
      // Charger la progression après avoir chargé les aspirants
      if (aspirantsData.length > 0) {
        loadProgressForAspirants(aspirantsData);
      }
    } catch (err) {
      console.error("Erreur chargement aspirants validés:", err);
    } finally {
      setLoadingValidated(false);
    }
  };

  const loadRejectedAspirants = async () => {
    setLoadingRejected(true);
    try {
      const res = await api.get("/aspirants/rejected");
      const aspirantsData = res.data?.aspirants || [];
      setRejectedAspirants(aspirantsData);
      // Charger la progression après avoir chargé les aspirants
      if (aspirantsData.length > 0) {
        loadProgressForAspirants(aspirantsData);
      }
    } catch (err) {
      console.error("Erreur chargement aspirants refusés:", err);
    } finally {
      setLoadingRejected(false);
    }
  };

  // Charger la progression pour tous les aspirants (route admin spécifique)
  const loadProgressForAspirants = async (aspirantsList) => {
    if (!aspirantsList || aspirantsList.length === 0) return;

    setLoadingProgress(true);
    try {
      // Utiliser la nouvelle route admin qui charge directement toutes les progressions
      const [cgRes, sylRes] = await Promise.all([
        api.get(`/adminStats/progress/CG`).catch(err => {
          console.error(`❌ Erreur CG pour admin:`, err?.response?.status, err?.response?.data);
          return { data: [] };
        }),
        api.get(`/adminStats/progress/SYL`).catch(err => {
          console.error(`❌ Erreur SYL pour admin:`, err?.response?.status, err?.response?.data);
          return { data: [] };
        })
      ]);

      const cgData = cgRes?.data || [];
      const sylData = sylRes?.data || [];
      
      // Combiner toutes les progressions
      const combinedCG = {};
      const combinedSYL = {};
      
      cgData.forEach(r => {
        const id = String(r.aspirant_id ?? r.id ?? r.user_id ?? "");
        if (id && id !== "undefined" && id !== "null" && id !== "") {
          const done = Number(r.done ?? 0) || 0;
          const total = Number(r.total ?? 0) || 0;
          let percent = r.progress != null ? Number(r.progress) : null;
          if (percent === null || !Number.isFinite(percent)) {
            percent = total > 0 ? Math.round((done / total) * 100) : 0;
          }
          combinedCG[id] = { 
            done, 
            total, 
            percent: Math.max(0, Math.min(100, Math.round(percent))) 
          };
        }
      });
      
      sylData.forEach(r => {
        const id = String(r.aspirant_id ?? r.id ?? r.user_id ?? "");
        if (id && id !== "undefined" && id !== "null" && id !== "") {
          const done = Number(r.done ?? 0) || 0;
          const total = Number(r.total ?? 0) || 0;
          let percent = r.progress != null ? Number(r.progress) : null;
          if (percent === null || !Number.isFinite(percent)) {
            percent = total > 0 ? Math.round((done / total) * 100) : 0;
          }
          combinedSYL[id] = { 
            done, 
            total, 
            percent: Math.max(0, Math.min(100, Math.round(percent))) 
          };
        }
      });
      
      // Fusionner avec les progressions existantes
      setProgressCGByAspirant(prev => ({ ...prev, ...combinedCG }));
      setProgressSYLByAspirant(prev => ({ ...prev, ...combinedSYL }));
    } catch (err) {
      console.error("Erreur chargement progression:", err);
    } finally {
      setLoadingProgress(false);
    }
  };

  // Fonction pour obtenir la progression d'un aspirant
  const getAspirantProgress = (aspirant) => {
    const aspirantId = String(aspirant.aspirant_id ?? aspirant.id ?? "");
    const isCG = aspirant.parcours === "Chef Guide" || aspirant.parcours === "CG";
    const isSYL = aspirant.parcours === "Senior Youth Leader" || aspirant.parcours === "SYL";
    
    if (isCG && progressCGByAspirant[aspirantId]) {
      return progressCGByAspirant[aspirantId];
    }
    if (isSYL && progressSYLByAspirant[aspirantId]) {
      return progressSYLByAspirant[aspirantId];
    }
    return null;
  };

  // Fonction pour rendre la barre de progression
  const renderProgressBar = (aspirant) => {
    const progress = getAspirantProgress(aspirant);
    
    if (loadingProgress) {
      return (
        <div className="progress" style={{ height: 18 }}>
          <div className="progress-bar bg-light" style={{ width: "30%" }} />
        </div>
      );
    }
    
    if (!progress) {
      return <span className="text-muted">—</span>;
    }
    
    const percent = Math.max(0, Math.min(100, progress.percent || 0));
    const label = `${progress.done}/${progress.total}`;
    const barClass = aspirant.parcours === "Chef Guide" || aspirant.parcours === "CG" 
      ? "bg-info" 
      : "bg-success";
    
    return (
      <div>
        <div className="small text-muted mb-1">{label}</div>
        <div className="progress" style={{ height: 18 }}>
          <div
            className={`progress-bar ${barClass}`}
            role="progressbar"
            style={{ width: `${percent}%` }}
            aria-valuenow={percent}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {percent}%
          </div>
        </div>
      </div>
    );
  };

  const openModal = (aspirant) => {
    setSelectedAspirant(aspirant);
    setComment(aspirant.rejection_comment || "");
    setModalError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAspirant(null);
    setComment("");
    setModalError("");
  };

  const handleValidation = async (action) => {
    if (!selectedAspirant) return;
    
    // Si on refuse, le commentaire est obligatoire
    if (action === "reject" && (!comment || !comment.trim())) {
      setModalError("Le commentaire est obligatoire pour refuser une inscription.");
      return;
    }
    
    // Réinitialiser l'erreur si tout est OK
    setModalError("");
    setSubmitting(true);
    try {
      const response = await api.patch(`/aspirants/${selectedAspirant.aspirant_id}/validate`, {
        action: action,
        comment: action === "reject" ? comment.trim() : null
      });
      
      // Recharger les listes
      await loadAspirants();
      await loadValidatedAspirants();
      await loadRejectedAspirants();
      closeModal();
      
      // Afficher un message de succès
      if (action === "validate") {
        alert("Elève validé avec succès !");
      } else {
        alert("Inscription refusée.");
      }
    } catch (err) {
      console.error("Erreur validation:", err);
      const errorMessage = err?.response?.data?.error || err?.message || "Erreur lors de la validation";
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    // Utiliser la fonction utilitaire qui masque l'URL du backend
    return getFrontFileUrl(filePath);
  };

  return (
    <div className="container-fluid py-4">
      <h2 className="mb-4 text-primary">Gerer les élèves</h2>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Liste des élèves en attente */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-warning text-dark">
          <h5 className="mb-0">
            <i className="bi bi-hourglass-split me-2"></i>
            Elèves en attente de validation
          </h5>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : aspirants.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-check-circle text-success" style={{ fontSize: "3rem" }}></i>
              <p className="mt-3 text-muted">Aucun élève en attente de validation.</p>
            </div>
          ) : (
            <>
              {/* Version desktop : tableau */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '120px' }}>Nom</th>
                      <th>Matricule</th>
                      <th>Église</th>
                      <th>District</th>
                      <th>Région</th>
                      <th>Parcours</th>
                      <th>Progression</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aspirants.map((aspirant) => (
                      <tr key={aspirant.aspirant_id}>
                        <td className="fw-semibold">{aspirant.name}</td>
                        <td>
                          {aspirant.matricule ? (
                            <span className="badge bg-success">{aspirant.matricule}</span>
                          ) : (
                            <span className="badge bg-warning text-dark">En attente</span>
                          )}
                        </td>
                        <td>{aspirant.eglise_name || "—"}</td>
                        <td>{aspirant.district_name || "—"}</td>
                        <td>{aspirant.region_name || "—"}</td>
                        <td>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </td>
                        <td style={{ minWidth: 200 }}>{renderProgressBar(aspirant)}</td>
                        <td className="text-center">
                          {!aspirant.is_validated_by_region ? (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openModal(aspirant)}
                            >
                              <i className="bi bi-eye me-1"></i>
                              Examiner
                            </button>
                          ) : (
                            <span className="badge bg-success">Validé</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Version mobile : cartes */}
              <div className="d-md-none">
                {aspirants.map((aspirant) => (
                  <div key={aspirant.aspirant_id} className="card mb-3 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-1">{aspirant.name}</h6>
                          <div className="mb-2">
                            <small className="text-muted d-block">Matricule:</small>
                            {aspirant.matricule ? (
                              <span className="badge bg-success">{aspirant.matricule}</span>
                            ) : (
                              <span className="badge bg-warning text-dark">En attente</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">Église:</small>
                        <span>{aspirant.eglise_name || "—"}</span>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">District:</small>
                        <span>{aspirant.district_name || "—"}</span>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted d-block">Région:</small>
                        <span>{aspirant.region_name || "—"}</span>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">Progression:</small>
                        {renderProgressBar(aspirant)}
                      </div>
                      
                      <div className="d-grid">
                        {!aspirant.is_validated_by_region ? (
                          <button
                            className="btn btn-sm btn-primary w-100"
                            onClick={() => openModal(aspirant)}
                          >
                            <i className="bi bi-eye me-1"></i>
                            Examiner
                          </button>
                        ) : (
                          <span className="badge bg-success w-100 text-center py-2">Validé</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modale de validation */}
      {showModal && selectedAspirant && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,.5)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Détails de l'élève</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  disabled={submitting}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Nom :</strong> {selectedAspirant.name}
                  </div>
                  <div className="col-md-6">
                    <strong>Date de naissance :</strong>{" "}
                    {selectedAspirant.birth_date
                      ? new Date(selectedAspirant.birth_date).toLocaleDateString("fr-FR")
                      : "—"}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Téléphone :</strong> {selectedAspirant.phone || "—"}
                  </div>
                  <div className="col-md-6">
                    <strong>Parcours :</strong>{" "}
                    <span className="badge bg-info text-dark">
                      {formatParcours(selectedAspirant.parcours)}
                    </span>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Église :</strong> {selectedAspirant.eglise_name || "—"}
                  </div>
                  <div className="col-md-6">
                    <strong>District :</strong> {selectedAspirant.district_name || "—"}
                  </div>
                </div>

                <div className="mb-3">
                  <strong>Documents :</strong>
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {selectedAspirant.carte_bapteme && (
                      <a
                        href={getFileUrl(selectedAspirant.carte_bapteme)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="bi bi-file-earmark-pdf me-1"></i>
                        Carte de baptême
                      </a>
                    )}
                    {selectedAspirant.recommandation && (
                      <a
                        href={getFileUrl(selectedAspirant.recommandation)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="bi bi-file-earmark-pdf me-1"></i>
                        Recommandation
                      </a>
                    )}
                    {selectedAspirant.certificat && (
                      <a
                        href={getFileUrl(selectedAspirant.certificat)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="bi bi-file-earmark-pdf me-1"></i>
                        {selectedAspirant.parcours === "Senior Youth Leader" ? "Certificat Séminaire de 10h" : "Certificat BST"}
                      </a>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="comment" className="form-label">
                    Commentaire {selectedAspirant.is_validated_by_region ? "" : "(obligatoire pour refus)"}
                  </label>
                  <textarea
                    id="comment"
                    className={`form-control ${modalError && !comment.trim() ? "is-invalid" : ""}`}
                    rows="3"
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value);
                      // Effacer l'erreur quand l'utilisateur commence à taper
                      if (modalError) setModalError("");
                    }}
                    placeholder="Ajouter un commentaire..."
                    required={false}
                  />
                  {modalError && (
                    <div className="invalid-feedback d-block">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {modalError}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={submitting}
                >
                  Annuler
                </button>
                {!selectedAspirant.is_validated_by_region && (
                  <>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => handleValidation("reject")}
                      disabled={submitting || !comment.trim()}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Envoi...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-x-circle me-1"></i>
                          Refuser
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => handleValidation("validate")}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Envoi...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-1"></i>
                          Valider
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des élèves validés */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-success text-white">
          <h5 className="mb-0">
            <i className="bi bi-check-circle me-2"></i>
            Elèves validés
          </h5>
        </div>
        <div className="card-body">
          {loadingValidated ? (
            <div className="text-center py-4">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : validatedAspirants.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox text-muted" style={{ fontSize: "3rem" }}></i>
              <p className="mt-3 text-muted">Aucun élève validé pour le moment.</p>
            </div>
          ) : (
            <>
              {/* Version desktop : tableau */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '120px' }}>Nom</th>
                      <th>Matricule</th>
                      <th>Église</th>
                      <th>District</th>
                      <th>Région</th>
                      <th>Parcours</th>
                      <th>Progression</th>
                      <th>Date de validation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validatedAspirants.map((aspirant) => (
                      <tr key={aspirant.aspirant_id}>
                        <td className="fw-semibold">{aspirant.name}</td>
                        <td>
                          <span className="badge bg-success">{aspirant.matricule || "—"}</span>
                        </td>
                        <td>{aspirant.eglise_name || "—"}</td>
                        <td>{aspirant.district_name || "—"}</td>
                        <td>{aspirant.region_name || "—"}</td>
                        <td>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </td>
                        <td style={{ minWidth: 200 }}>{renderProgressBar(aspirant)}</td>
                        <td>
                          {aspirant.validated_at
                            ? new Date(aspirant.validated_at).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Version mobile : cartes */}
              <div className="d-md-none">
                {validatedAspirants.map((aspirant) => (
                  <div key={aspirant.aspirant_id} className="card mb-3 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-1">{aspirant.name}</h6>
                          <div className="mb-2">
                            <small className="text-muted d-block">Matricule:</small>
                            {aspirant.matricule ? (
                              <span className="badge bg-success">{aspirant.matricule}</span>
                            ) : (
                              <span className="badge bg-secondary">—</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">Église:</small>
                        <span>{aspirant.eglise_name || "—"}</span>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">District:</small>
                        <span>{aspirant.district_name || "—"}</span>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted d-block">Région:</small>
                        <span>{aspirant.region_name || "—"}</span>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted d-block">Progression:</small>
                        {renderProgressBar(aspirant)}
                      </div>

                      {aspirant.validated_at && (
                        <div>
                          <small className="text-muted d-block">Date de validation:</small>
                          <small>
                            {new Date(aspirant.validated_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Liste des inscriptions refusées */}
      <div className="card shadow-sm">
        <div className="card-header bg-danger text-white">
          <h5 className="mb-0">
            <i className="bi bi-x-circle me-2"></i>
            Inscriptions refusées
          </h5>
        </div>
        <div className="card-body">
          {loadingRejected ? (
            <div className="text-center py-4">
              <div className="spinner-border text-danger" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : rejectedAspirants.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-check-circle text-success" style={{ fontSize: "3rem" }}></i>
              <p className="mt-3 text-muted">Aucune inscription refusée pour le moment.</p>
            </div>
          ) : (
            <>
              {/* Version desktop : tableau */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '120px' }}>Nom</th>
                      <th>Église</th>
                      <th>District</th>
                      <th>Région</th>
                      <th>Parcours</th>
                      <th>Progression</th>
                      <th>Commentaire de refus</th>
                      <th>Date de refus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejectedAspirants.map((aspirant) => (
                      <tr key={aspirant.aspirant_id}>
                        <td className="fw-semibold">{aspirant.name}</td>
                        <td>{aspirant.eglise_name || "—"}</td>
                        <td>{aspirant.district_name || "—"}</td>
                        <td>{aspirant.region_name || "—"}</td>
                        <td>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </td>
                        <td style={{ minWidth: 200 }}>{renderProgressBar(aspirant)}</td>
                        <td>
                          {aspirant.rejection_comment ? (
                            <span className="text-danger fst-italic">{aspirant.rejection_comment}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {aspirant.validated_at ? (
                            new Date(aspirant.validated_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Version mobile : cartes */}
              <div className="d-md-none">
                {rejectedAspirants.map((aspirant) => (
                  <div key={aspirant.aspirant_id} className="card mb-3 shadow-sm border-danger">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="fw-semibold mb-1">{aspirant.name}</h6>
                        </div>
                        <div>
                          <span className="badge bg-info text-dark">{formatParcours(aspirant.parcours)}</span>
                        </div>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">Église:</small>
                        <span>{aspirant.eglise_name || "—"}</span>
                      </div>
                      
                      <div className="mb-2">
                        <small className="text-muted d-block">District:</small>
                        <span>{aspirant.district_name || "—"}</span>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted d-block">Région:</small>
                        <span>{aspirant.region_name || "—"}</span>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted d-block">Progression:</small>
                        {renderProgressBar(aspirant)}
                      </div>

                      {aspirant.rejection_comment && (
                        <div className="mb-2">
                          <small className="text-muted d-block">Commentaire de refus:</small>
                          <span className="text-danger fst-italic">{aspirant.rejection_comment}</span>
                        </div>
                      )}
                      
                      {aspirant.validated_at && (
                        <div>
                          <small className="text-muted d-block">Date de refus:</small>
                          <small>
                            {new Date(aspirant.validated_at).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

